import {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Grid,
  Icon,
  Input,
  Popup,
  Select,
  DropdownProps,
  Loader,
  Ref,
} from "semantic-ui-react";
import FetchAPI from "../../../../../../api/FetchAPI";
import { useSelector } from "react-redux";
import {
  getCurrentProjectID,
  toastError,
} from "../../../../../../app_shared_functions";
import { getProjectsObject } from "../../../../../../selectors/projects";
import useIsMountedRef from "../../../../../../custom-hooks/useIsMountedRef";
import { SubnetPool } from "./types";
import AddressingType from "./AddressingType";

type SubnetPoolProps = {
  onChange: (event: React.FormEvent<HTMLElement>, data: DropdownProps) => void;
  region: string;
  subnetPool: string;
  subnetPoolRef: MutableRefObject<HTMLElement | null>;
  errorClassName: string;
  addressingType: string;
};

const SubnetPools = ({
  onChange,
  region,
  subnetPool,
  subnetPoolRef,
  errorClassName,
  addressingType,
}: SubnetPoolProps) => {
  const [pools, setPools] = useState<SubnetPool[]>([]);
  const [loading, setLoading] = useState(true);

  const projects = useSelector(getProjectsObject);
  const projectId = getCurrentProjectID(projects, region);
  const isMountedRef = useIsMountedRef();

  const fetchSubnetPools = useCallback(() => {
    FetchAPI.Networking.Subnets.getSubnetPools({
      region: region,
      project_id: projectId,
    })
      .then((response) => {
        if (!isMountedRef.current) return;
        const data = response.data as SubnetPool[];
        setPools(data);
      })
      .catch((err) => toastError(err, "Getting subnet pools failed!"))
      .finally(() => setLoading(false));
  }, [region, projectId, isMountedRef]);

  const options = useMemo(() => {
    return pools.map((pool) => ({
      key: pool.id,
      value: pool.id,
      text: `${pool.name} (Prefix: /${pool.default_prefixlen})`,
    }));
  }, [pools]);

  useEffect(() => {
    if (options.length === 1) {
      const defaultId = options[0].value;
      onChange({} as React.FormEvent<HTMLElement>, {
        name: "subnetPool",
        value: defaultId,
      });
    }
  }, [options, subnetPool, onChange]);

  useEffect(fetchSubnetPools, [fetchSubnetPools]);

  return (
    <Ref innerRef={subnetPoolRef}>
      <Grid.Row>
        <Grid.Column textAlign="left" width={8} className="flex vcenter">
          <h5>
            Subnet Pool
            <Popup
              trigger={
                <Icon
                  name="warning circle"
                  color="grey"
                  size="small"
                  className="margin-left-10"
                />
              }
            >
              Choose Subnet Pool
            </Popup>
          </h5>
        </Grid.Column>
        <Grid.Column textAlign="left" width={8}>
          {loading ? (
            <div className="loader-wrapper">
              <Loader size="mini" active className="one-liner">
                loading...
              </Loader>
            </div>
          ) : options?.length ? (
            <Select
              icon="chevron circle down"
              className={`select-box full ${errorClassName}`}
              placeholder="Choose Subnet Pool"
              name="subnetPool"
              options={options}
              value={subnetPool}
              onChange={onChange}
            />
          ) : (
            <Input disabled={true} value="No subnet pool found!" />
          )}
        </Grid.Column>
        {subnetPool && (
          <AddressingType
            pool={pools.find((pool) => pool.id === subnetPool)!}
            onChange={onChange}
            addressingType={addressingType}
          />
        )}
      </Grid.Row>
    </Ref>
  );
};

export default memo(SubnetPools);
